import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import ls from "local-storage";
import AuthApi from "../../Services/Authapi";
import swal from "sweetalert";
import { createSwalLoaderNode } from "../../Layouts/swalLoader";
import { promptRejectionRemark } from "../UserCheck/Check";
import "./ReminderPopup.css";
import {
  getEventId,
  getEventTitle,
  getCleanEventTitle,
  getCategory,
  getEffectiveCategory,
  getEventStartMoment,
  getEventOwnerId,
  getLeaveOwnerName,
  getLeaveStatus,
  formatMeetingTitle,
  isMeetingRecipient,
  getStableEventKey
} from "../../../utils/calendarReminderUtils";

const STORAGE_PREFIX = "ems_reminder_dismissed_";
const TIMESTAMP_STORAGE_PREFIX = "ems_reminder_time_";
const CHECK_INTERVAL_MS = 30 * 1000;
const FETCH_RANGE_PAST_DAYS = 1;
const FETCH_RANGE_FUTURE_DAYS = 3;
const MEETING_24H_WINDOW_MIN = 1380.0;
const MEETING_24H_WINDOW_MAX = 1440.0;

const getReminderKey = (type, eventId, dateStr = "", userId = null) => {
  const userSeg = userId !== null && userId !== undefined && userId !== "" ? `u${userId}_` : "";
  return dateStr
    ? `${STORAGE_PREFIX}${userSeg}${type}_${eventId}_${dateStr}`
    : `${STORAGE_PREFIX}${userSeg}${type}_${eventId}`;
};

const getReminderTimeStorageKey = (key) =>
  `${TIMESTAMP_STORAGE_PREFIX}${key}`;

const isAlreadyDismissed = (...keys) => {
  try {
    const isValDismissed = (val) =>
      val === "1" || val === 1 || val === true || val === "true";

    for (const k of keys) {
      if (!k) continue;
      if (isValDismissed(ls.get(k))) return true;
      const raw = localStorage.getItem(k);
      if (isValDismissed(raw) || raw === '"1"') return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

const markAsDismissed = (...keys) => {
  try {
    for (const k of keys) {
      if (!k) continue;
      ls.set(k, "1");
      localStorage.setItem(k, "1");
    }
  } catch (e) {
    // ignore
  }
};

const getStableReminderTimeLabel = (key, nowMoment) => {
  const timeKey = getReminderTimeStorageKey(key);
  let savedFormattedTime = null;
  try {
    savedFormattedTime = ls.get(timeKey) || localStorage.getItem(timeKey);
  } catch (e) {
    // ignore
  }

  if (savedFormattedTime && savedFormattedTime !== "null" && savedFormattedTime !== "undefined") {
    return String(savedFormattedTime).replace(/^"|"$/g, "");
  }

  const formattedTime = nowMoment.format("h:mm A");
  try {
    ls.set(timeKey, formattedTime);
    localStorage.setItem(timeKey, formattedTime);
  } catch (e) {
    // ignore
  }

  return formattedTime;
};

const BellIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const ReminderPopup = () => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [fetchedEvents, setFetchedEvents] = useState([]);

  const seenAutoOpenKeysRef = useRef(new Set());
  const blockedAutoOpenKeysRef = useRef(new Set());

  const currentUserId = (() => {
    try {
      const user = ls.get("user");
      return user?.id ?? user?.Id ?? null;
    } catch (e) {
      return null;
    }
  })();

  const isAdmin = (() => {
    try {
      const roles = ls.get("roles");
      if (Array.isArray(roles)) {
        return roles.some((r) =>
          ["super admin", "admin"].includes(String(r).toLowerCase())
        );
      }
      if (typeof roles === "string") {
        return ["super admin", "admin"].includes(roles.toLowerCase());
      }
      return false;
    } catch (e) {
      return false;
    }
  })();

  const fetchReminderEvents = useCallback(async () => {
    try {
      const now = moment();
      const response = await AuthApi.getDashboardCalendar({
        start: now.clone().subtract(FETCH_RANGE_PAST_DAYS, "day").format("YYYY-MM-DD"),
        end: now.clone().add(FETCH_RANGE_FUTURE_DAYS, "days").format("YYYY-MM-DD"),
      });

      if (response && response.status === true && response.data) {
        const nextEvents = Array.isArray(response.data.events) ? response.data.events : [];
        setFetchedEvents(nextEvents);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const buildReminders = useCallback((rawEvents) => {
    const now = moment();
    const todayStr = now.format("YYYY-MM-DD");
    const remindersList = [];
    const seenReminderIds = new Set();

    const dedupedRawEvents = [];
    const seenRawKeys = new Set();

    (rawEvents || []).forEach((event) => {
      const explicitId = getEventId(event);
      const cat = getEffectiveCategory(event);
      const cleanTitle = getCleanEventTitle(event).toLowerCase().replace(/[^a-z0-9]/g, "");
      const startMoment = getEventStartMoment(event, false);
      const startStr = startMoment.isValid() ? startMoment.format("YYYYMMDDHHmm") : "";

      const idKey = explicitId ? `${cat}|${explicitId}` : null;
      const contentKey = `${cat}|${cleanTitle}|${startStr}`;

      if ((idKey && seenRawKeys.has(idKey)) || (contentKey && seenRawKeys.has(contentKey))) {
        return;
      }

      if (idKey) seenRawKeys.add(idKey);
      if (contentKey) seenRawKeys.add(contentKey);
      dedupedRawEvents.push(event);
    });

    dedupedRawEvents.forEach((event) => {
      const category = getCategory(event);
      const effectiveCategory = getEffectiveCategory(event);
      let explicitId = getEventId(event);
      if (explicitId === null || typeof explicitId === "undefined") {
        const cleanTitle = getCleanEventTitle(event).toLowerCase().replace(/[^a-z0-9]/g, "");
        const startMoment = getEventStartMoment(event, true);
        const startStr = startMoment.isValid() ? startMoment.format("YYYYMMDD") : "";
        const owner = getEventOwnerId(event) ?? "";
        explicitId = `${cleanTitle}_${startStr}_${owner}`;
      }
      const eventId = String(explicitId);

      if (category === "leave") {
        const leaveStatus = getLeaveStatus(event);
        const leaveOwnerId = getEventOwnerId(event);
        const isMyLeave =
          currentUserId !== null &&
          leaveOwnerId !== null &&
          String(leaveOwnerId) === String(currentUserId);

        if (!isAdmin && !isMyLeave) return;

        const startMoment = getEventStartMoment(event, true);
        if (!startMoment.isValid()) return;

        const diffDays = startMoment.clone().startOf("day").diff(now.clone().startOf("day"), "days");
        const leaveOwnerName = getLeaveOwnerName(event);

        if (leaveStatus === "pending") {
          // Pending leave reminders should ONLY show to Admin for approval
          if (!isAdmin) return;

          // Skip pending leave reminders if older than 60 days
          if (diffDays < -60) return;

          const leaveType = event.LeaveType || event.leaveType || event.leave_type || "";
          const userKey = getReminderKey("leave-pending", eventId, todayStr, currentUserId);
          const key = getReminderKey("leave-pending", eventId, todayStr);
          const legacyKey = getReminderKey("leave-pending", eventId);
          const legacyUserKey = getReminderKey("leave-pending", eventId, "", currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const shouldAutoOpen = isAdmin && !isMyLeave;
          const autoOpenKey = `leave-autoopen-${eventId}-${leaveStatus}-${todayStr}`;
          const reminderId = `leave-pending-${eventId}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);
            let displayTitle = "Leave Request Pending";
            if (isAdmin && leaveOwnerName && !isMyLeave) {
              displayTitle = `${leaveOwnerName} - ${leaveType ? leaveType + " " : ""}Pending Leave`;
            } else if (leaveType) {
              displayTitle = `${leaveType} Pending Approval`;
            }

            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: shouldAutoOpen,
              autoOpenKey: shouldAutoOpen ? autoOpenKey : undefined,
              type: "leave",
              category: "leave-pending",
              icon: "L",
              typeLabel: "Leave Ticket Pending",
              title: displayTitle,
              subtitle: `Status: Pending approval - ${startMoment.format("ddd, MMM D")}`,
              timeChip: timeChipLabel,
              urgent: true,
              sortWeight: 15,
            });
          }
          return;
        }

        if (leaveStatus !== "pending") {
          const userKey = getReminderKey("leave-pending", eventId, todayStr, currentUserId);
          const key = getReminderKey("leave-pending", eventId, todayStr);
          const legacyKey = getReminderKey("leave-pending", eventId);
          const legacyUserKey = getReminderKey("leave-pending", eventId, "", currentUserId);

          const numericId = String(eventId).replace(/^leave-/i, "");
          const numUserKey = getReminderKey("leave-pending", numericId, todayStr, currentUserId);
          const numKey = getReminderKey("leave-pending", numericId, todayStr);
          const numLegacyKey = getReminderKey("leave-pending", numericId);
          const numLegacyUserKey = getReminderKey("leave-pending", numericId, "", currentUserId);

          markAsDismissed(
            userKey, key, legacyKey, legacyUserKey,
            numUserKey, numKey, numLegacyKey, numLegacyUserKey
          );
        }

        // 2) APPROVED / DECLINED / UNAPPROVED / CANCELLED LEAVE NOTIFICATIONS TO USER
        const cleanLeaveStatus = String(leaveStatus || "").toLowerCase();
        if (isMyLeave && (cleanLeaveStatus === "approved" || cleanLeaveStatus === "declined" || cleanLeaveStatus === "rejected" || cleanLeaveStatus === "unapproved" || cleanLeaveStatus === "cancelled")) {
          const userKey = getReminderKey(`leave-${cleanLeaveStatus}`, eventId, "", currentUserId);
          const key = getReminderKey(`leave-${cleanLeaveStatus}`, eventId);
          const legacyKey = getReminderKey(`leave-${cleanLeaveStatus}`, eventId, todayStr);
          const legacyUserKey = getReminderKey(`leave-${cleanLeaveStatus}`, eventId, todayStr, currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const autoOpenKey = `leave-status-autoopen-${eventId}-${cleanLeaveStatus}-${todayStr}`;
          const reminderId = `leave-status-${eventId}-${cleanLeaveStatus}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);

            const leaveType = event.LeaveType || event.leaveType || event.leave_type || "";
            let typeLabel = "Leave Ticket Status";
            let displayTitle = `Your ${leaveType ? leaveType + " " : ""}Leave Request ${cleanLeaveStatus.toUpperCase()}`;
            let subtitle = `Date: ${startMoment.format("ddd, MMM D, YYYY")}`;
            let categoryClass = `leave-${cleanLeaveStatus}`;

            if (cleanLeaveStatus === "approved") {
              typeLabel = "Leave Ticket Approved";
              displayTitle = `Your ${leaveType ? leaveType + " " : ""}Leave Approved`;
              subtitle = `Approved by Admin - ${startMoment.format("ddd, MMM D, YYYY")}`;
              categoryClass = "leave-approved";
            } else if (cleanLeaveStatus === "declined" || cleanLeaveStatus === "rejected" || cleanLeaveStatus === "unapproved") {
              const isUnapproved = cleanLeaveStatus === "unapproved";
              typeLabel = isUnapproved ? "Leave Ticket Unapproved" : "Leave Ticket Declined";
              displayTitle = `Your ${leaveType ? leaveType + " " : ""}Leave ${isUnapproved ? "Unapproved" : "Declined"}`;
              subtitle = `${isUnapproved ? "Unapproved" : "Declined"} by Admin - ${startMoment.format("ddd, MMM D, YYYY")}`;
              categoryClass = "leave-declined";
            } else if (cleanLeaveStatus === "cancelled") {
              typeLabel = "Leave Ticket Cancelled";
              displayTitle = `Your ${leaveType ? leaveType + " " : ""}Leave Cancelled`;
              subtitle = `Cancelled - ${startMoment.format("ddd, MMM D, YYYY")}`;
              categoryClass = "leave-cancelled";
            }

            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: true,
              autoOpenKey: autoOpenKey,
              type: "leave",
              category: categoryClass,
              icon: "L",
              typeLabel,
              title: displayTitle,
              subtitle,
              timeChip: timeChipLabel,
              urgent: cleanLeaveStatus === "declined" || cleanLeaveStatus === "rejected" || cleanLeaveStatus === "unapproved",
              sortWeight: 18,
            });
          }
        }

        // 3) UPCOMING LEAVE REMINDERS (Today / Tomorrow)
        if (cleanLeaveStatus === "approved" && (diffDays === 0 || diffDays === 1)) {
          const leaveType = event.LeaveType || event.leaveType || event.leave_type || "";
          const userKey = getReminderKey(`leave-${diffDays}d`, eventId, todayStr, currentUserId);
          const key = getReminderKey(`leave-${diffDays}d`, eventId, todayStr);
          const legacyKey = getReminderKey(`leave-${diffDays}d`, eventId);
          const legacyUserKey = getReminderKey(`leave-${diffDays}d`, eventId, "", currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const shouldAutoOpen = false;
          const reminderId = `leave-${diffDays}d-${eventId}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);
            const labelText = diffDays === 0 ? "Today's Leave" : "Tomorrow's Leave";
            const displayTitle =
              isAdmin && leaveOwnerName && !isMyLeave
                ? `${leaveOwnerName} - ${leaveType ? leaveType + " " : ""}${labelText}`
                : leaveType ? `${leaveType} (${labelText})` : labelText;

            const typeLabel = diffDays === 0 ? "Today's Leave" : "Upcoming Leave Tomorrow";
            const subtitle = diffDays === 0
              ? "Leave starts today"
              : `Leave starts tomorrow (${startMoment.format("ddd, MMM D")})`;

            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: shouldAutoOpen,
              type: "leave",
              category: "leave-approved",
              icon: "L",
              typeLabel,
              title: displayTitle,
              subtitle,
              timeChip: timeChipLabel,
              urgent: false,
              sortWeight: 30,
            });
          }
          return;
        }
        return;
      }

      if (category === "time-entry" || effectiveCategory === "time-entry" || category === "time_entry") {
        const isApprovedVal = event.is_approved ?? event.IsApproved ?? event.isApproved;
        const isApprovedStr = String(isApprovedVal ?? "").trim().toLowerCase();

        // 0 = Approved, 2 = Rejected/Declined, 1/null = Pending
        const isApproved = isApprovedStr === "0" || isApprovedStr === "approved";
        const isRejected = isApprovedStr === "2" || isApprovedStr === "rejected" || isApprovedStr === "declined";
        const isPending = !isApproved && !isRejected;

        const startMoment = getEventStartMoment(event, true);
        const rawOwnerName = getLeaveOwnerName(event) || event.EmployeeName || event.employeeName || event.Subject || event.title;
        const cleanName = String(rawOwnerName || "Employee")
          .replace(/\s*-\s*Pending Time Entry$/i, "")
          .replace(/\s*-\s*Time Entry$/i, "")
          .trim();
        const leaveOwnerId = getEventOwnerId(event);
        const isMyEntry =
          currentUserId !== null &&
          leaveOwnerId !== null &&
          String(leaveOwnerId) === String(currentUserId);

        // 1) Pending Time Entry: Show ONLY to Admin (Do NOT show in User's reminder!)
        if (isPending) {
          if (!isAdmin) return;

          const isLastDayOfMonth = now.date() >= now.clone().endOf("month").date() - 2;
          const typeSub = isLastDayOfMonth ? "monthend" : "pending";
          const userKey = getReminderKey(`time-entry-${typeSub}`, eventId, todayStr, currentUserId);
          const key = getReminderKey(`time-entry-${typeSub}`, eventId, todayStr);
          const legacyKey = getReminderKey(`time-entry-${typeSub}`, eventId);
          const legacyUserKey = getReminderKey(`time-entry-${typeSub}`, eventId, "", currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const shouldAutoOpen = isAdmin;
          const autoOpenKey = `time-entry-autoopen-${eventId}-${typeSub}-${todayStr}`;
          const reminderId = `time-entry-${eventId}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);
            const displayTitle =
              isAdmin && cleanName
                ? `${cleanName} - Time Entry`
                : "Time Entry";

            const rawDesc = event.Description || event.description || event.reason || "";
            const reasonText = String(rawDesc)
              .replace(/^Reason:\s*/i, "")
              .trim();

            let typeLabel = "Pending Time Entry";
            let subtitle = reasonText && reasonText !== "None" ? `Reason: ${reasonText}` : "Time Entry Pending Approval";

            if (isLastDayOfMonth) {
              typeLabel = "Month End: Unapproved Time Entry";
              subtitle = `Approval required before month end - ${startMoment.isValid() ? startMoment.format("ddd, MMM D") : todayStr}`;
            }

            remindersList.push({
              id: reminderId,
              sourceId: event.SourceId || event.id || eventId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: shouldAutoOpen,
              autoOpenKey: shouldAutoOpen ? autoOpenKey : undefined,
              type: "time-entry",
              category: "time-entry",
              icon: "T",
              typeLabel,
              title: displayTitle,
              subtitle,
              timeChip: timeChipLabel,
              urgent: true,
              sortWeight: isLastDayOfMonth ? 5 : 20,
            });
          }
          return;
        }

        // Auto-dismiss pending keys whenever status is non-pending (Approved or Rejected)
        if (!isPending) {
          const userKeyP = getReminderKey("time-entry-pending", eventId, todayStr, currentUserId);
          const keyP = getReminderKey("time-entry-pending", eventId, todayStr);
          const legacyKeyP = getReminderKey("time-entry-pending", eventId);
          const legacyUserKeyP = getReminderKey("time-entry-pending", eventId, "", currentUserId);
          const userKeyM = getReminderKey("time-entry-monthend", eventId, todayStr, currentUserId);
          const keyM = getReminderKey("time-entry-monthend", eventId, todayStr);
          const legacyKeyM = getReminderKey("time-entry-monthend", eventId);
          const legacyUserKeyM = getReminderKey("time-entry-monthend", eventId, "", currentUserId);

          const numericId = String(eventId).replace(/^time-entry-/i, "");
          const numUserKeyP = getReminderKey("time-entry-pending", numericId, todayStr, currentUserId);
          const numKeyP = getReminderKey("time-entry-pending", numericId, todayStr);
          const numLegacyKeyP = getReminderKey("time-entry-pending", numericId);
          const numLegacyUserKeyP = getReminderKey("time-entry-pending", numericId, "", currentUserId);

          markAsDismissed(
            userKeyP, keyP, legacyKeyP, legacyUserKeyP,
            userKeyM, keyM, legacyKeyM, legacyUserKeyM,
            numUserKeyP, numKeyP, numLegacyKeyP, numLegacyUserKeyP
          );
        }

        // 2) Approved Time Entry: Show in GREEN to USER/Employee after Admin approves
        if (isApproved) {
          if (isAdmin && !isMyEntry) return;

          const userKey = getReminderKey("time-entry-approved", eventId, "", currentUserId);
          const key = getReminderKey("time-entry-approved", eventId);
          const legacyKey = getReminderKey("time-entry-approved", eventId, todayStr);
          const legacyUserKey = getReminderKey("time-entry-approved", eventId, todayStr, currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const autoOpenKey = `time-entry-approved-autoopen-${eventId}-${todayStr}`;
          const reminderId = `time-entry-approved-${eventId}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);
            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: true,
              autoOpenKey,
              type: "time-entry-approved",
              category: "time-entry-approved",
              icon: "✓",
              typeLabel: "Time Entry Approved",
              title: "Your time entry approved",
              subtitle: `Approved for ${startMoment.isValid() ? startMoment.format("ddd, MMM D") : todayStr}`,
              timeChip: timeChipLabel,
              urgent: false,
              sortWeight: 8,
            });
          }
          return;
        }

        // 3) Rejected Time Entry: Show in RED/URGENT to USER/Employee after Admin rejects
        if (isRejected) {
          if (isAdmin && !isMyEntry) return;

          const userKey = getReminderKey("time-entry-rejected", eventId, "", currentUserId);
          const key = getReminderKey("time-entry-rejected", eventId);
          const legacyKey = getReminderKey("time-entry-rejected", eventId, todayStr);
          const legacyUserKey = getReminderKey("time-entry-rejected", eventId, todayStr, currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const autoOpenKey = `time-entry-rejected-autoopen-${eventId}-${todayStr}`;
          const reminderId = `time-entry-rejected-${eventId}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);

            const rawRemark = event.remark || event.Remark || event.description || event.Description || "";
            const remarkText = String(rawRemark).replace(/^Reason:\s*/i, "").trim();
            const subtitleText = remarkText && remarkText !== "None"
              ? `Rejected by Admin: ${remarkText}`
              : `Rejected for ${startMoment.isValid() ? startMoment.format("ddd, MMM D") : todayStr}`;

            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: true,
              autoOpenKey,
              type: "time-entry-rejected",
              category: "leave-declined",
              icon: "✕",
              typeLabel: "Time Entry Rejected",
              title: "Your time entry was rejected",
              subtitle: subtitleText,
              timeChip: timeChipLabel,
              urgent: true,
              sortWeight: 9,
            });
          }
          return;
        }
        return;
      }

      if (effectiveCategory === "meeting") {
        const startMoment = getEventStartMoment(event, false);
        if (!startMoment.isValid()) return;

        if (!isMeetingRecipient(event, { isAdmin, currentUserId })) return;

        const minutesUntil = startMoment.diff(now, "minutes", true);
        const displayMinutesUntil = Math.max(1, Math.ceil(minutesUntil));
        const stableKey = getStableEventKey(event);
        const autoOpenKey = `meeting-soon-${stableKey}-${todayStr}-${startMoment.format("HH-mm")}`;
        const meetingTitle = formatMeetingTitle(event);
        const key24hUser = getReminderKey("meeting-24h", stableKey, startMoment.format("YYYY-MM-DD-HH-mm"), currentUserId);
        const key24h = getReminderKey("meeting-24h", stableKey, startMoment.format("YYYY-MM-DD-HH-mm"));

        if (minutesUntil <= 10 && minutesUntil >= 0) {
          markAsDismissed(key24hUser, key24h);

          const key10mUser = getReminderKey("meeting-10m", stableKey, startMoment.format("YYYY-MM-DD-HH-mm"), currentUserId);
          const key10m = getReminderKey("meeting-10m", stableKey, startMoment.format("YYYY-MM-DD-HH-mm"));
          const dismissKeys = [key10mUser, key10m];

          const timeChipLabel = getStableReminderTimeLabel(key10mUser, now);
          const reminderId = `meeting-10m-${stableKey}-${startMoment.format("YYYYMMDDHHmm")}`;

          const cleanTitleStr = getCleanEventTitle(event).toLowerCase().replace(/[^a-z0-9]/g, "");
          const contentReminderId = `meeting-10m-${cleanTitleStr}-${startMoment.format("YYYYMMDDHHmm")}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId) && !seenReminderIds.has(contentReminderId)) {
            seenReminderIds.add(reminderId);
            seenReminderIds.add(contentReminderId);
            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: key10mUser,
              legacyDismissKey: key10m,
              autoOpen: true,
              autoOpenKey,
              type: "meeting-soon",
              category: "meeting-soon",
              icon: "M",
              typeLabel: "Meeting Starting Soon",
              title: meetingTitle,
              subtitle: `In ${displayMinutesUntil} min${displayMinutesUntil > 1 ? "s" : ""} (${startMoment.format("h:mm A")})`,
              timeChip: timeChipLabel,
              urgent: true,
              sortWeight: 10,
            });
          }
          return;
        }

        if (minutesUntil >= MEETING_24H_WINDOW_MIN && minutesUntil <= MEETING_24H_WINDOW_MAX) {
          const dismissKeys = [key24hUser, key24h];
          const timeChipLabel = getStableReminderTimeLabel(key24hUser, now);
          const reminderId = `meeting-24h-${stableKey}-${startMoment.format("YYYYMMDDHHmm")}`;

          const cleanTitleStr = getCleanEventTitle(event).toLowerCase().replace(/[^a-z0-9]/g, "");
          const contentReminderId = `meeting-24h-${cleanTitleStr}-${startMoment.format("YYYYMMDDHHmm")}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId) && !seenReminderIds.has(contentReminderId)) {
            seenReminderIds.add(reminderId);
            seenReminderIds.add(contentReminderId);
            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: key24hUser,
              legacyDismissKey: key24h,
              autoOpen: false,
              type: "meeting",
              category: "meeting",
              icon: "M",
              typeLabel: "Upcoming Meeting (24 Hours)",
              title: meetingTitle,
              subtitle: `Tomorrow at ${startMoment.format("h:mm A")}`,
              timeChip: timeChipLabel,
              urgent: false,
              sortWeight: 40,
            });
          }
        }
        return;
      }

      if (category === "holiday") {
        const startMoment = getEventStartMoment(event, true);
        if (!startMoment.isValid()) return;

        const diffDays = startMoment.clone().startOf("day").diff(now.clone().startOf("day"), "days");
        if (diffDays === 0 || diffDays === 1) {
          const userKey = getReminderKey(`holiday-${diffDays}d`, eventId, todayStr, currentUserId);
          const key = getReminderKey(`holiday-${diffDays}d`, eventId, todayStr);
          const legacyKey = getReminderKey(`holiday-${diffDays}d`, eventId);
          const legacyUserKey = getReminderKey(`holiday-${diffDays}d`, eventId, "", currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const reminderId = `holiday-${diffDays}d-${eventId}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);
            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: false,
              type: "holiday",
              category: "holiday",
              icon: "H",
              typeLabel: diffDays === 0 ? "Today's Holiday" : "Holiday Tomorrow",
              title: getCleanEventTitle(event),
              subtitle: diffDays === 0
                ? "Enjoy your holiday!"
                : `Holiday tomorrow (${startMoment.format("ddd, MMM D")})`,
              timeChip: timeChipLabel,
              urgent: false,
              sortWeight: diffDays === 0 ? 35 : 45,
            });
          }
        }
        return;
      }

      if (category === "event" || category === "upcoming") {
        const startMoment = getEventStartMoment(event, true);
        if (!startMoment.isValid()) return;

        const diffDays = startMoment.clone().startOf("day").diff(now.clone().startOf("day"), "days");
        if (diffDays === 0 || diffDays === 1) {
          const userKey = getReminderKey(`event-${diffDays}d`, eventId, todayStr, currentUserId);
          const key = getReminderKey(`event-${diffDays}d`, eventId, todayStr);
          const legacyKey = getReminderKey(`event-${diffDays}d`, eventId);
          const legacyUserKey = getReminderKey(`event-${diffDays}d`, eventId, "", currentUserId);
          const dismissKeys = [userKey, key, legacyKey, legacyUserKey];

          const timeChipLabel = getStableReminderTimeLabel(userKey, now);
          const reminderId = `event-${diffDays}d-${eventId}`;

          if (!isAlreadyDismissed(...dismissKeys) && !seenReminderIds.has(reminderId)) {
            seenReminderIds.add(reminderId);
            remindersList.push({
              id: reminderId,
              dismissKeys,
              dismissKey: userKey,
              legacyDismissKey: key,
              autoOpen: false,
              type: "event",
              category: "event",
              icon: "E",
              typeLabel: diffDays === 0 ? "Today's Event" : "Upcoming Event Tomorrow",
              title: getCleanEventTitle(event),
              subtitle: diffDays === 0
                ? `Event today (${startMoment.format("ddd, MMM D")})`
                : `Event tomorrow (${startMoment.format("ddd, MMM D")})`,
              timeChip: timeChipLabel,
              urgent: false,
              sortWeight: diffDays === 0 ? 30 : 50,
            });
          }
        }
      }
    });

    remindersList.sort((a, b) => a.sortWeight - b.sortWeight);
    return remindersList;
  }, [isAdmin, currentUserId]);

  const computeReminders = useCallback(() => {
    const list = buildReminders(fetchedEvents);
    setReminders(list);
  }, [fetchedEvents, buildReminders]);

  useEffect(() => {
    fetchReminderEvents();
  }, [fetchReminderEvents]);

  useEffect(() => {
    computeReminders();
    const interval = setInterval(computeReminders, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [computeReminders]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchReminderEvents();
      computeReminders();
    };

    window.addEventListener("cherrypik_refresh_notifications", handleRefresh);
    window.addEventListener("cherrypik_new_notification", handleRefresh);
    window.addEventListener("storage", handleRefresh);

    return () => {
      window.removeEventListener("cherrypik_refresh_notifications", handleRefresh);
      window.removeEventListener("cherrypik_new_notification", handleRefresh);
      window.removeEventListener("storage", handleRefresh);
    };
  }, [fetchReminderEvents, computeReminders]);

  useEffect(() => {
    const autoOpenReminders = reminders.filter((r) => r.autoOpen && r.autoOpenKey);
    let shouldOpen = false;
    autoOpenReminders.forEach((r) => {
      if (!seenAutoOpenKeysRef.current.has(r.autoOpenKey) && !blockedAutoOpenKeysRef.current.has(r.autoOpenKey)) {
        seenAutoOpenKeysRef.current.add(r.autoOpenKey);
        shouldOpen = true;
      }
    });

    Array.from(seenAutoOpenKeysRef.current).forEach((key) => {
      if (!autoOpenReminders.some((r) => r.autoOpenKey === key)) {
        seenAutoOpenKeysRef.current.delete(key);
        blockedAutoOpenKeysRef.current.delete(key);
      }
    });

    if (shouldOpen) {
      setIsOpen(true);
    }
  }, [reminders]);

  const closePanel = () => {
    reminders.forEach((r) => {
      if (r.autoOpen && r.autoOpenKey) {
        blockedAutoOpenKeysRef.current.add(r.autoOpenKey);
      }
    });
    setIsOpen(false);
  };

  const handleDismiss = (reminder) => {
    const keysToDismiss = reminder.dismissKeys || [reminder.dismissKey, reminder.legacyDismissKey].filter(Boolean);
    markAsDismissed(...keysToDismiss);
    if (reminder.autoOpenKey) blockedAutoOpenKeysRef.current.add(reminder.autoOpenKey);
    setReminders((prev) => prev.filter((r) => r.id !== reminder.id));
  };

  const handleDismissAll = () => {
    reminders.forEach((r) => {
      const keysToDismiss = r.dismissKeys || [r.dismissKey, r.legacyDismissKey].filter(Boolean);
      markAsDismissed(...keysToDismiss);
      if (r.autoOpenKey) blockedAutoOpenKeysRef.current.add(r.autoOpenKey);
    });
    setReminders([]);
    setIsOpen(false);
  };

  const handleQuickApprove = async (reminder) => {
    const user = ls("user");
    const userName = `${user?.firstName || 'Admin'} ${user?.lastName || ''}`.trim();
    const sourceId = reminder.sourceId || reminder.eventId || String(reminder.id).replace(/^time-entry-/, "");

    const ticketUpdateData = {
      is_approved: "0",
      userName: userName,
    };

    swal({
      title: "Are you sure?",
      text: "Do you want to approve this entry?",
      icon: "warning",
      buttons: ["Cancel", "Yes"],
    }).then(async (confirm) => {
      if (confirm) {
        swal({
          title: "Approving Time Entry...",
          content: createSwalLoaderNode(),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });

        let res = await AuthApi.approveTime(ticketUpdateData, sourceId);
        if (res && res.status === true) {
          handleDismiss(reminder);
          window.dispatchEvent(new Event("cherrypik_refresh_notifications"));
          window.dispatchEvent(new Event("storage"));
          swal("Done!", "You have approved the entry!", "success");
        } else {
          swal("Error!", "There was an error approving the entry.", "error");
        }
      }
    });
  };

  const handleQuickReject = async (reminder) => {
    const user = ls("user");
    const userName = `${user?.firstName || 'Admin'} ${user?.lastName || ''}`.trim();
    const sourceId = reminder.sourceId || reminder.eventId || String(reminder.id).replace(/^time-entry-/, "");

    const rejectionRemark = await promptRejectionRemark("Reject Time Entry");
    if (rejectionRemark) {
      const ticketUpdateData = {
        is_approved: "2",
        userName: userName,
        remark: rejectionRemark,
      };

      swal({
        title: "Rejecting Time Entry...",
        content: createSwalLoaderNode(),
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });

      let res = await AuthApi.approveTime(ticketUpdateData, sourceId);
      if (res && res.status === true) {
        handleDismiss(reminder);
        window.dispatchEvent(new Event("cherrypik_refresh_notifications"));
        window.dispatchEvent(new Event("storage"));
        swal("Done!", "You have rejected the entry!", "success");
      } else {
        swal("Error!", "There was an error rejecting the entry.", "error");
      }
    }
  };

  const handleCardClick = (reminder, e) => {
    if (e && e.target && e.target.closest(".ems-reminder-card-dismiss-btn")) {
      return;
    }
    if (e && e.target && e.target.closest(".ems-reminder-card-actions")) {
      return;
    }

    // Automatically dismiss the clicked reminder so user/admin doesn't have to manually click clear/X
    const keysToDismiss = reminder.dismissKeys || [reminder.dismissKey, reminder.legacyDismissKey].filter(Boolean);
    markAsDismissed(...keysToDismiss);
    if (reminder.autoOpenKey) blockedAutoOpenKeysRef.current.add(reminder.autoOpenKey);
    setReminders((prev) => prev.filter((r) => r.id !== reminder.id));

    const cat = String(reminder.category || reminder.type || "").toLowerCase();

    let targetRoute = "";
    if (cat === "time-entry" || cat === "time_entry" || cat === "time-entry-approved") {
      targetRoute = "/time-entries";
    } else if (cat === "leave") {
      targetRoute = "/leave-tickets";
    } else if (cat === "holiday") {
      targetRoute = "/holiday-leaves";
    } else if (cat === "event" || cat === "upcoming") {
      targetRoute = "/upcoming-event";
    }

    setIsOpen(false);
    if (targetRoute) {
      if (history && typeof history.push === "function") {
        history.push(targetRoute);
      } else {
        window.location.href = targetRoute;
      }
    }
  };

  const unreadCount = reminders.length;

  return (
    <>
      <button
        className="ems-reminder-bell-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        title="Reminders"
        aria-label={`Reminders (${unreadCount} pending)`}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="ems-reminder-bell-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="ems-reminder-panel-overlay" onClick={closePanel} />
          <div className="ems-reminder-panel" role="dialog" aria-label="Reminders panel">
            <div className="ems-reminder-panel-header">
              <div className="ems-reminder-panel-header-left">
                <div className="ems-reminder-panel-header-icon">R</div>
                <div>
                  <h3>Reminders</h3>
                  <p>
                    {unreadCount === 0
                      ? "All caught up!"
                      : `${unreadCount} pending reminder${unreadCount > 1 ? "s" : ""}`}
                  </p>
                </div>
              </div>
              <div className="ems-reminder-panel-header-actions">
                {unreadCount > 0 && (
                  <button
                    className="ems-reminder-panel-clear-btn"
                    onClick={handleDismissAll}
                    title="Dismiss all reminders"
                  >
                    Clear All
                  </button>
                )}
                <button
                  className="ems-reminder-panel-close-btn"
                  onClick={closePanel}
                  title="Close panel"
                >
                  x
                </button>
              </div>
            </div>

            <div className="ems-reminder-panel-body">
              {reminders.length === 0 ? (
                <div className="ems-reminder-empty">
                  <div className="ems-reminder-empty-icon">OK</div>
                  <p>No reminders right now.</p>
                  <p style={{ fontSize: "12px", marginTop: "4px", color: "#b0bec5" }}>
                    Check back later!
                  </p>
                </div>
              ) : (
                reminders.map((reminder) => (
                  <ReminderCard
                    key={reminder.id}
                    reminder={reminder}
                    onDismiss={handleDismiss}
                    onClick={handleCardClick}
                    onApprove={handleQuickApprove}
                    onReject={handleQuickReject}
                    isAdmin={isAdmin}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const ReminderCard = ({ reminder, onDismiss, onClick, onApprove, onReject, isAdmin }) => (
  <div
    className={`ems-reminder-card ems-reminder-card-${reminder.category}`}
    onClick={(e) => onClick && onClick(reminder, e)}
    style={{ cursor: "pointer" }}
  >
    <div className="ems-reminder-card-stripe" />
    <div className="ems-reminder-card-inner">
      <div className="ems-reminder-card-row1">
        <span className="ems-reminder-card-icon">{reminder.icon}</span>
        <div className="ems-reminder-card-text">
          <div className="ems-reminder-card-type-label">{reminder.typeLabel}</div>
          <div className="ems-reminder-card-title">{reminder.title}</div>
          {reminder.subtitle && <div className="ems-reminder-card-subtitle">{reminder.subtitle}</div>}
        </div>
        <button
          className="ems-reminder-card-dismiss-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(reminder);
          }}
          title="Dismiss this reminder"
          aria-label="Dismiss reminder"
        >
          x
        </button>
      </div>
      <div className="ems-reminder-card-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
        <span className="ems-reminder-card-time-chip">{reminder.timeChip}</span>
        {isAdmin && reminder.category === "time-entry" && reminder.urgent && (
          <div className="ems-reminder-card-actions" style={{ display: "flex", gap: "6px" }}>
            <button
              className="ems-reminder-action-approve"
              onClick={(e) => {
                e.stopPropagation();
                onApprove && onApprove(reminder);
              }}
              style={{
                backgroundColor: "#16a34a",
                color: "#ffffff",
                border: "none",
                borderRadius: "4px",
                padding: "4px 10px",
                fontSize: "11px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "3px"
              }}
              title="Approve Time Entry"
            >
              ✓ Approve
            </button>
            <button
              className="ems-reminder-action-reject"
              onClick={(e) => {
                e.stopPropagation();
                onReject && onReject(reminder);
              }}
              style={{
                backgroundColor: "#dc2626",
                color: "#ffffff",
                border: "none",
                borderRadius: "4px",
                padding: "4px 10px",
                fontSize: "11px",
                fontWeight: "bold",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "3px"
              }}
              title="Reject Time Entry"
            >
              ✗ Reject
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default ReminderPopup;
